<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="hrissecondary"
        class="white--text px-4 ml-2"
        v-on="on"
      >
        Bulk Edit
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Bulk Edit Type of Misconduct"
      text="Bulk edit code and / or job group policy for type of misconduct"
      class="mb-0"
    > 
      <form>
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="12"
            >
              <v-autocomplete
                v-model="versionFilter"
                v-validate="'required'"
                :error-messages="errors.collect('versionFilter')"
                data-vv-name="version filter"  
                :items="typeOfMisconductRevisions"
                label="Selected Version"
                placeholder="Please select the version for bulk edit"
                item-text="name"
                item-value="id"
                outlined
                @change="loadTypeOfMisconducts"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="policyFilter"
                v-validate="'required'"
                :error-messages="errors.collect('policyFilter')"
                data-vv-name="group filter" 
                :items="jobGroupPoliciesWithNull"
                label="Selected Job Group Policy"
                placeholder="Please select the job group policy for bulk edit"
                item-text="name"
                item-value="id"
                outlined
                @change="loadTypeOfMisconducts"
              />
            </v-col>
            
            <v-row
              v-if="!loading && versionFilter && policyFilter && !noRecord"
              no-gutters
            >
              <v-col
                cols="12"
              >
                <v-text-field
                  v-model="code"
                  v-validate="'required|integer|min_value:1'"
                  :error-messages="errors.collect('code')"
                  data-vv-name="code"
                  type="number"
                  label="First New Parent Code Number"
                  outlined
                  :min="1"
                  @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                >
                  <v-tooltip 
                    slot="append"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        color="warning"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>Smallest parent code number based on the selected version and group to bulk edit: <b>{{ smallestParentNumber }}</b> </span>
                  </v-tooltip>
                </v-text-field>
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="group"
                  v-validate="'required'"
                  :error-messages="errors.collect('group')"
                  data-vv-name="group" 
                  :items="jobGroupPoliciesWithNull"
                  label="Job Group Policy"
                  item-text="name"
                  item-value="id"
                  outlined
                />
              </v-col>
            </v-row>
        
            <v-row v-if="!loading && noRecord">
              <v-col
                cols="12"
                class="px-6 pb-2 mt-n3"
              >
                <v-icon
                  class="pr-1"
                  color="red"
                >
                  mdi-information-outline
                </v-icon>
                <span
                  class="red--text"
                >
                  No type of misconducts record found. Please select another filter.
                </span>
              </v-col>
            </v-row>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"  
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="loading || noRecord"
          color="hrissecondary" 
          class="white--text px-10"        
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      jobGroupPolicies: {
        type: Array,
        default: null,
      },
      typeOfMisconductRevisions: {
        type: Array,
        default: null,
      },
    },
    data () {
      return {
        loading: false,
        progressing: false,
        dialog: null,
        versionFilter: null,
        policyFilter: null,
        code: null,
        group: null,
        noRecord: false,
        smallestParentNumber: 0,
        typeOfMisconducts: [],
      }
    },
    computed: {
      jobGroupPoliciesWithNull () {
        let temp = [ ...this.jobGroupPolicies ]

        temp.push({ id: 'null', name: 'N/A' })

        return temp
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()
      },
    },
    methods: {
      loadTypeOfMisconducts () {
        if (this.versionFilter && this.policyFilter) {
          this.loading = true
          this.$api.typeOfMisconducts().list({ version_id: this.versionFilter, group_id: this.policyFilter })
            .then(res => {
              this.typeOfMisconducts = _.orderBy(res.data, ['code'], ['asc'])

              if (this.typeOfMisconducts.length <= 0) {
                this.noRecord = true
              } else {
                let parentNumbers = []
                
                this.typeOfMisconducts.map(item => {
                  const code = parseInt(item.code.split('.', 1))
                  
                  parentNumbers.push(code)
                })
                parentNumbers.sort((a, b) => a - b)

                parentNumbers = [...new Set(parentNumbers)]

                this.smallestParentNumber = parentNumbers[0]
                this.noRecord = false
              }

              this.loading = false
            })
        }
      },
      prepareFormData () {
        return {
          version_filter: this.versionFilter,
          policy_filter: this.policyFilter === 'null' ? null : this.policyFilter,
          difference: this.code - this.smallestParentNumber,
          job_group_policy_id: this.group === 'null' ? null : this.group,
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.typeOfMisconducts().updateMany(this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('updated', res.data)                
                  this.$snack({ message: res.data.status })

                  this.$validator.reset()
                  this.dialog = false               
                }
              })
              .catch((err) => {   
                if (err.response.status === 422) {              
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.message, 
                  })
                } else {          
                  this.$snack({ 
                    color: 'error',
                    message: 'Unable to bulk update the type of misconducts', 
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
  
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px
}
</style>
